import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import type { OfflineResourceConfig, StorageAdapter } from "../../../types"
import { PRE_ORDER_TABLE } from "./db"
import { PRE_ORDER_FLOW_TABLE } from "../pre-order-flow/db"
import { PRE_ORDER_MESSAGE_TABLE } from "../pre-order-message/db"
import { PRE_ORDER_SMS_MESSAGE_TABLE } from "../pre-order-sms-message/db"
import { PRE_ORDER_EMAIL_TABLE } from "../pre-order-email/db"
import { PRE_ORDER_SUGGESTION_TABLE } from "../../ai/pre-order-suggestion/db"
import { PRE_ORDER_FLOW_SUGGESTION_TABLE } from "../../ai/pre-order-flow-suggestion/db"

/**
 * Evict cascade (STK-1238): a pre-order that left the viewer's scope arrives
 * as `{op:"delete", evict:true}` with NO child events (a pure header change
 * emits only `pre-order.updated`), so the dependent local rows must be
 * dropped here. Flows are collected FIRST so their own
 * pre-order-flow-suggestions (`targetId` = flow id) can cascade before the
 * flow rows are deleted. The distinct `flow-suggestions` resource
 * (FLOW_SUGGESTION_TABLE, "flowSuggestions" in Dexie) is deliberately NOT
 * touched — that one is company-wide per its server visibility filter,
 * unlike the group-scoped pre-order-flow-suggestions cascaded here.
 */
export async function cascadePreOrderEvict(
  id: string,
  storage: StorageAdapter
): Promise<void> {
  const flows = await storage.getAll<{ id: string }>(PRE_ORDER_FLOW_TABLE, {
    preOrderId: id,
  })
  for (const flow of flows) {
    const preOrderFlowSuggestions = await storage.getAll<{ id: string }>(
      PRE_ORDER_FLOW_SUGGESTION_TABLE,
      { targetId: flow.id }
    )
    for (const suggestion of preOrderFlowSuggestions) {
      await storage.delete(PRE_ORDER_FLOW_SUGGESTION_TABLE, suggestion.id)
    }
    await storage.delete(PRE_ORDER_FLOW_TABLE, flow.id)
  }
  for (const table of [
    PRE_ORDER_MESSAGE_TABLE,
    PRE_ORDER_SMS_MESSAGE_TABLE,
    PRE_ORDER_EMAIL_TABLE,
  ]) {
    const junctions = await storage.getAll<{ id: string }>(table, {
      preOrderId: id,
    })
    for (const junction of junctions) {
      await storage.delete(table, junction.id)
    }
  }
  const suggestions = await storage.getAll<{ id: string }>(
    PRE_ORDER_SUGGESTION_TABLE,
    { targetId: id }
  )
  for (const suggestion of suggestions) {
    await storage.delete(PRE_ORDER_SUGGESTION_TABLE, suggestion.id)
  }
}

export const preOrderConfig: OfflineResourceConfig<PreOrder> = {
  name: "pre-orders",
  // Server visibility is GROUP-scoped: wiped + cold re-pulled on a
  // scope change (STK-1238).
  groupScoped: true,
  // HOT: keep only the rolling window on device, bounded by the server
  // `windowFloor`. Windowed over BOTH recap axes (delivery_date + order_date)
  // server-side, open-ended into the future. STK-1322.
  tier: "hot",
  // 7-day rolling window (STK-1927). Tighter than the original 30 d: the pull
  // floor is `MIN(syncId in-window)`, so a narrower window only bites once the
  // low-syncId anchors are lifted by the re-stamp (STK-1925) — the two ship
  // together. Open-ended into the future still keeps every upcoming delivery HOT.
  window: { days: 7 },
  // ~2.7 KB/row (a 1000-row page is ~2.6 MB / ~0.4s stringify). Smaller pages
  // keep the V2 API event loop responsive during cold-start storms.
  pageSize: 250,
  endpoints: {
    full: "/api/v2/orders/pre-orders/sync",
    delta: "/api/v2/orders/pre-orders/sync/changes",
    push: "/api/v2/orders/pre-orders/sync/changes",
  },
  tableName: PRE_ORDER_TABLE,
  primaryKey: "id",
  indexes: {
    companyId: "companyId",
    userId: "userId",
    status: "status",
    partnerId: "partnerId",
    whatsappChatId: "whatsappChatId",
    emailId: "emailId",
    smsChatId: "smsChatId",
    deletedAt: "deletedAt",
  },
  applyChange: (local, patch) => ({ ...local, ...patch }),
  // Recap "show deleted orders" toggle reads tombstones from the offline
  // store, so delete events must keep the row with `deletedAt` set instead
  // of removing it. `usePreOrders` filters tombstones by default.
  softDelete: true,
  onEvict: cascadePreOrderEvict,
}
